import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Compass, HeartHandshake, ShieldCheck, Sparkles, MessageCircle } from "lucide-react";
import { listPousadas, getPousada } from "@/lib/publicApi";
import { PousadaCard } from "@/components/public/PousadaCard";
import { CTAWhatsApp } from "@/components/public/CTAWhatsApp";
import { BRAND } from "@/lib/brand";
import { fotoUrl } from "@/lib/publicApi";

export default function Home() {
  const [pousadas, setPousadas] = useState([]);
  const [precos, setPrecos] = useState({}); // {pousadaId: menorDiaria}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const list = await listPousadas();
        if (cancel) return;
        setPousadas(list);
        // Para pousadas em destaque (até 6), buscar quartos para preço "a partir de"
        const destaques = list.slice(0, 6);
        const results = await Promise.all(
          destaques.map((p) => getPousada(p.slug || p.id).catch(() => null))
        );
        if (cancel) return;
        const map = {};
        results.forEach((d) => {
          if (!d || !Array.isArray(d.quartos)) return;
          const vals = d.quartos
            .map((q) => Number(q.valor_diaria || 0))
            .filter((v) => v > 0);
          if (vals.length) map[d.id] = Math.min(...vals);
        });
        setPrecos(map);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const destaques = useMemo(() => pousadas.slice(0, 6), [pousadas]);
  const totalPousadas = pousadas.length;
  const heroFoto = useMemo(() => {
    const first = pousadas.find((p) => p.foto_principal_id);
    return first ? fotoUrl(first.foto_principal_id) : null;
  }, [pousadas]);

  return (
    <>
      <Helmet>
        <title>{`${BRAND.name} — pousadas selecionadas no interior`}</title>
        <meta name="description" content={`${BRAND.tagline}. Reserve diretamente com quem opera cada pousada da ${BRAND.name}.`} />
        <link rel="canonical" href="/" />
        <meta property="og:title" content={`${BRAND.name} — pousadas selecionadas`} />
        <meta property="og:description" content={BRAND.tagline} />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* HERO */}
      <section data-testid="home-hero" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#083A6B] via-[#083A6B] to-[#052B50]" />
        {heroFoto && (
          <img
            src={heroFoto}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-overlay"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#083A6B]/60 via-transparent to-transparent" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-[#F5F1E8]">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#D4A652] mb-6">
              <Sparkles size={12} /> Rede de pousadas selecionadas
            </div>
            <h1 className="font-public-title text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
              Hospedagem com <span className="italic text-[#D4A652]">alma de interior</span>,
              reserva feita por quem conhece cada quarto.
            </h1>
            <p className="text-base lg:text-lg text-[#F5F1E8]/85 max-w-2xl leading-relaxed mb-10">
              Pousadas operadas com cuidado próprio, com atendimento próximo e personalizado.
              Fale com a gente, confirme condições reais e reserve com segurança.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/pousadas"
                data-testid="hero-cta-pousadas"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-[#D4A652] hover:bg-[#B8902F] text-white text-sm font-medium transition-colors"
              >
                Conheça nossas pousadas <ArrowRight size={16} />
              </Link>
              <CTAWhatsApp variant="secondary" label="Falar no WhatsApp" testid="hero-cta-whatsapp" className="!border-white/30 !text-white hover:!bg-white/10" />
            </div>

            <div className="mt-12 flex flex-wrap gap-x-10 gap-y-4 text-[12px] text-[#F5F1E8]/75">
              <div><span className="font-public-title text-2xl text-[#D4A652]">{totalPousadas || "+"}</span><span className="ml-2">pousadas na rede</span></div>
              <div><span className="font-public-title text-2xl text-[#D4A652]">24/7</span><span className="ml-2">atendimento humano</span></div>
              <div><span className="font-public-title text-2xl text-[#D4A652]">0%</span><span className="ml-2">taxa oculta</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* DESTAQUES */}
      <section data-testid="home-destaques" className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold mb-3">
              Pousadas em destaque
            </div>
            <h2 className="font-public-title text-3xl lg:text-4xl text-stone-900 leading-tight max-w-2xl">
              Endereços escolhidos a dedo para quem busca descanso de verdade.
            </h2>
          </div>
          <Link
            to="/pousadas"
            data-testid="destaques-ver-todas"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#083A6B] hover:gap-2.5 transition-all"
          >
            Ver todas as pousadas <ArrowRight size={14} />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="aspect-[4/3] rounded-lg bg-stone-200/60 animate-pulse" />
            ))}
          </div>
        ) : destaques.length === 0 ? (
          <div className="text-center py-16 text-stone-500 text-sm">
            Em breve, nossas pousadas aparecerão aqui.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {destaques.map((p) => (
              <PousadaCard key={p.id} pousada={p} precoDesde={precos[p.id]} />
            ))}
          </div>
        )}
      </section>

      {/* DIFERENCIAIS */}
      <section data-testid="home-diferenciais" className="bg-[#F5F1E8] border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-24">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold mb-3">
              Por que reservar com a {BRAND.name}
            </div>
            <h2 className="font-public-title text-3xl lg:text-4xl text-stone-900 leading-tight">
              Uma rede feita por operadores, pensada para hóspedes.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <Diferencial
              icon={ShieldCheck}
              titulo="Reserva assistida"
              texto="Antes de confirmar, você fala com uma pessoa real que conhece a pousada, os quartos e o entorno."
            />
            <Diferencial
              icon={HeartHandshake}
              titulo="Operação própria"
              texto="Cada pousada é operada diretamente, com atendimento direto e transparente. Isso significa flexibilidade real e preço justo."
            />
            <Diferencial
              icon={Compass}
              titulo="Endereços selecionados"
              texto="Pousadas escolhidas com cuidado pela localização, estrutura e qualidade do atendimento. Nenhuma entra na rede sem passar por auditoria própria."
            />
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section data-testid="home-cta" className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="relative overflow-hidden rounded-2xl bg-[#083A6B] text-[#F5F1E8] px-8 py-14 lg:px-16 lg:py-20">
          <div className="absolute inset-0 opacity-[0.06]" aria-hidden="true">
            <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#D4A652]" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#F5F1E8]" />
          </div>
          <div className="relative max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#D4A652] mb-5">
              <MessageCircle size={12} /> Atendimento direto
            </div>
            <h2 className="font-public-title text-3xl lg:text-5xl leading-[1.1] mb-5">
              Prefere conversar antes de reservar?
            </h2>
            <p className="text-base lg:text-lg text-[#F5F1E8]/85 leading-relaxed mb-8 max-w-xl">
              Nosso time responde no WhatsApp em minutos e ajuda você a escolher a
              pousada e o quarto certos para o seu estilo de viagem.
            </p>
            <CTAWhatsApp variant="primary" label="Chamar no WhatsApp agora" testid="home-cta-whatsapp" />
          </div>
        </div>
      </section>
    </>
  );
}

function Diferencial({ icon: Icon, titulo, texto }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="w-11 h-11 rounded-md bg-[#083A6B]/8 text-[#083A6B] flex items-center justify-center">
        <Icon size={20} strokeWidth={1.6} />
      </div>
      <h3 className="font-public-title text-xl text-stone-900 leading-tight">{titulo}</h3>
      <p className="text-sm text-stone-600 leading-relaxed">{texto}</p>
    </div>
  );
}
